import {
  Download as DownloadIcon,
  DownloadCloud,
  WifiOff,
  Zap,
  Activity,
  Lock,
  ShieldCheck,
  EyeOff,
  Plus,
  Minus,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { fetchLatestDownloads } from "../../utils/dynamicDownloadManager";
import "./Download.css";
import PublicHeader from "../../components/PublicHeader/PublicHeader";
import Footer from "../../components/Footer/Footer";
import ReadyToTransform from "../../components/ReadyToTransform/ReadyToTransform";

const TEAL = "#00D1C1";

const DOWNLOAD_FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "How do I install TeamGrid?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "To install TeamGrid, download the desktop agent for Windows, macOS, or Linux from the Download page. After installation, sign in with your credentials to begin tracking.",
      },
    },
    {
      "@type": "Question",
      name: "Are there prerequisites for the desktop agent?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "The desktop agent is lightweight and does not require advanced hardware or administrative privileges, allowing it to run smoothly on supported devices.",
      },
    },
  ],
};

const DownloadPage = () => {
  const [downloadStatus, setDownloadStatus] = useState({});
  const [downloadUrls, setDownloadUrls] = useState({});
  const [versions, setVersions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [faqOpen, setFaqOpen] = useState({ 0: true });

  const toggleFaq = (i) => setFaqOpen((prev) => ({ ...prev, [i]: !prev[i] }));

  useEffect(() => {
    const loadDownloadUrls = async () => {
      try {
        setLoading(true);
        const downloads = await fetchLatestDownloads();
        const urls = {};
        const vers = {};
        Object.keys(downloads).forEach((platform) => {
          urls[platform] = downloads[platform].url;
          vers[platform] = downloads[platform].version;
        });
        setDownloadUrls(urls);
        setVersions(vers);
        setError(null);
      } catch (err) {
        console.error("Error loading downloads:", err);
        const baseUrl = "https://teamgrid.ai/downloads";
        setDownloadUrls({
          windows: `${baseUrl}/TeamGrid%20Setup%202.0.0.exe`,
          mac: `${baseUrl}/TeamGrid-2.0.0.dmg`,
          "mac-intel": `${baseUrl}/TeamGrid-2.0.0.dmg`,
          "mac-arm64": `${baseUrl}/TeamGrid-2.0.0-arm64.dmg`,
          ubuntu: `${baseUrl}/team-grid-desktop_1.6.0_amd64.deb`,
          "ubuntu-arm64": `${baseUrl}/team-grid-desktop_1.6.0_amd64.deb`,
        });
        setVersions({
          windows: "2.0.0",
          mac: "2.0.0",
          "mac-intel": "2.0.0",
          "mac-arm64": "2.0.0",
          ubuntu: "1.6.0",
          "ubuntu-arm64": "1.6.0",
        });
      } finally {
        setLoading(false);
      }
    };
    loadDownloadUrls();
  }, []);

  const handleDownload = async (platform) => {
    if (!downloadUrls[platform]) {
      alert("Download link not available for this platform");
      return;
    }
    setDownloadStatus((prev) => ({ ...prev, [platform]: "downloading" }));
    const link = document.createElement("a");
    link.href = downloadUrls[platform];
    const urlParts = downloadUrls[platform].split("/");
    const filename = urlParts[urlParts.length - 1] || `TeamGrid-${platform}`;
    link.download = decodeURIComponent(filename);
    link.target = "_self";
    link.rel = "noopener noreferrer";
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => {
      setDownloadStatus((prev) => ({ ...prev, [platform]: "success" }));
    }, 1500);
  };

  const displayVersion = versions["windows"] || versions["mac-arm64"] || versions["mac-intel"] || versions["ubuntu"] || "2.4.0";

  if (loading) {
    return (
      <div className="download-container">
        <PublicHeader />
        <div className="download-hero" style={{ minHeight: "60vh", justifyContent: "center" }}>
          <div className="download-hero-inner">
            <div className="animate-spin rounded-full h-12 w-12 border-2 border-[#00D1C1] border-t-transparent mx-auto" />
            <p style={{ color: "#475569", marginTop: 16 }}>Loading download links...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="download-container">
        <PublicHeader />
        <div className="download-hero" style={{ minHeight: "60vh", justifyContent: "center" }}>
          <p style={{ color: "#f43f5e" }}>{error}</p>
        </div>
        <Footer />
      </div>
    );
  }

  const WindowsLogo = () => (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor">
      <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-13.051-1.351" />
    </svg>
  );

  const AppleLogo = () => (
    <svg
      width="40"
      height="40"
      viewBox="0 0 24 24"
      fill="#ffffff"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Apple"
      style={{ display: "block" }}
    >
      <path
        fillRule="evenodd"
        d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.23-3.14-2.43-1.7-2.45-3-6.95-1.26-9.99 1.7-2.96 4.77-2.93 6.07-2.96 1.4-.03 2.74.96 3.6.96.86 0 2.47-1.18 4.16-.99 2.7.12 4.75 1.77 4.75 1.77-.07.03-2.61 1.5-2.58 4.46.03 2.92 2.6 4.31 2.6 4.31zM13 3.5c.73-.83 1.21-1.96 1.08-3.11-1.05.05-2.32.73-2.94 1.45-.63.71-1.18 1.87-1.04 2.98 1.17.08 2.37-.58 2.9-1.32z"
        aria-hidden
      />
    </svg>
  );

  const LinuxLogo = () => (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Tux penguin - Linux mascot */}
      <ellipse cx="12" cy="8.5" rx="4" ry="4.5" fill="#2D2D2D" />
      <path d="M6 8c0 3.3 2.7 6 6 6s6-2.7 6-6c0-1-.2-1.9-.6-2.8-.8.5-1.7.8-2.6.8s-1.8-.3-2.6-.8C6.2 6.1 6 7 6 8z" fill="#2D2D2D" />
      <path d="M8 13.5c0 2.2 1.8 4 4 4s4-1.8 4-4c0-.4 0-.8-.1-1.2-.6.4-1.3.6-2 .6s-1.4-.2-2-.6c0 .4-.1.8-.1 1.2z" fill="#fff" />
      <circle cx="9.5" cy="7.5" r="1.2" fill="#2D2D2D" />
      <circle cx="14.5" cy="7.5" r="1.2" fill="#2D2D2D" />
      <circle cx="9.7" cy="7.5" r="0.35" fill="#fff" />
      <circle cx="14.7" cy="7.5" r="0.35" fill="#fff" />
      <path d="M12 10.2c.6 0 1.2.2 1.6.5.1.4.2.8.2 1.2 0 1.2-1 2.2-2.2 2.2S9.8 13.1 9.8 11.9c0-.4.1-.8.2-1.2.4-.3 1-.5 1.6-.5z" fill="#2D2D2D" />
      <ellipse cx="8" cy="19" rx="1.8" ry="1.2" fill="#F8A31C" />
      <ellipse cx="16" cy="19" rx="1.8" ry="1.2" fill="#F8A31C" />
    </svg>
  );

  const desktopFeatures = [
    {
      icon: WifiOff,
      title: "Offline Mode",
      description: "Track work seamlessly even when your internet connection drops. Data syncs automatically.",
    },
    {
      icon: Zap,
      title: "Zero Latency",
      description: "Native performance ensures the app runs in the background without slowing down your PC.",
    },
    {
      icon: Activity,
      title: "Auto-Pause",
      description: "Intelligent idle detection automatically pauses tracking when you step away from your desk.",
    },
  ];

  const installSteps = [
    { num: 1, style: "filled", title: "Download & Open", desc: "Download the installer for your OS and double-click to launch the setup wizard." },
    { num: 2, style: "outline-brand", title: "Sign In", desc: "Log in with your TeamGrid credentials. SSO is supported for Enterprise plans." },
    { num: 3, style: "outline-slate", title: "Start Tracking", desc: "The app will minimize to your tray. Work as usual, and we'll handle the rest." },
  ];

  const securityItems = [
    { icon: ShieldCheck, title: "Bank-Level Security", sub: "SOC 2 Type II Compliant" },
    { icon: Lock, title: "End-to-End Encryption", sub: "Data encrypted at rest and in transit" },
    { icon: EyeOff, title: "Privacy Controls", sub: "User-controlled data visibility" },
  ];

  const faqItems = [
    {
      question: "How do I install TeamGrid?",
      answer:
        "Simply download the installer above for your specific operating system. Open the file and follow the on-screen instructions. No complex configuration is needed.",
    },
    {
      question: "Are there prerequisites for the desktop agent?",
      answer:
        "We support macOS 11.0+, Windows 10/11 (64-bit), and most modern Linux distributions (Ubuntu, Fedora). You need an active internet connection for the initial sign-in.",
    },
  ];

  return (
    <>
      <Helmet>
        <title>Download TeamGrid Desktop | Windows, Mac, Linux</title>
        <meta name="description" content="Download the TeamGrid desktop agent for Windows, macOS, or Linux. Lightweight automatic time tracking with offline mode and zero-latency AI insights." />
        <link rel="canonical" href="https://teamgrid.ai/download" />
        <script type="application/ld+json">{JSON.stringify(DOWNLOAD_FAQ_SCHEMA)}</script>
      </Helmet>
      <div className="download-container" data-page="download-public">
      <PublicHeader />
      <div className="pt-16 sm:pt-18 md:pt-20">
        {/* Hero / Downloads - full-bleed background */}
        <section className="download-hero">
          <div className="download-hero-inner">
            <div className="download-version-badge">
              <DownloadCloud size={12} stroke={TEAL} />
              <span>Version {displayVersion} Available</span>
            </div>
            <h1 className="download-hero-title">
              Download <span>TeamGrid</span> Desktop
            </h1>
            <p className="download-hero-subtitle">
              Experience native performance with automatic time tracking, offline mode, and zero-latency AI insights.
            </p>
          </div>

          <div className="download-cards">
            {/* macOS */}
            <div className="download-card">
              <div className="download-card-icon-wrap mac download-card-icon-wrap--image">
                <img
                  src="/Background+Shadow_newapple12.webp"
                  alt="Apple"
                  width={64}
                  height={64}
                  style={{ display: "block", width: 64, height: 64, objectFit: "contain" }}
                />
              </div>
              <h3 className="download-card-title">TeamGrid for macOS</h3>
              <p className="download-card-requirements">Requires macOS 11.0 or later</p>
              <div className="download-card-buttons">
                <button
                  type="button"
                  className="download-btn-outline"
                  onClick={() => handleDownload("mac-arm64")}
                  disabled={downloadStatus["mac-arm64"] === "downloading"}
                >
                  <span>Apple Silicon</span>
                  <span className="download-badge m1">M1/M2/M3</span>
                  <DownloadIcon size={16} style={{ marginLeft: "auto", color: TEAL }} />
                </button>
                <button
                  type="button"
                  className="download-btn-outline secondary"
                  onClick={() => handleDownload("mac-intel")}
                  disabled={downloadStatus["mac-intel"] === "downloading"}
                >
                  <span>Intel Processor</span>
                  <DownloadIcon size={16} style={{ marginLeft: "auto", color: "#A0A0A0" }} />
                </button>
              </div>
              <button
                type="button"
                className="download-card-link"
                onClick={() => alert("Go to Apple Menu > About This Mac to see your processor. M1/M2/M3 = Apple Silicon.")}
              >
                Not sure which processor?
              </button>
            </div>

            {/* Windows */}
            <div className="download-card">
              <div className="download-card-icon-wrap windows" style={{ color: "#000000" }}>
                <WindowsLogo />
              </div>
              <h3 className="download-card-title">TeamGrid for Windows</h3>
              <p className="download-card-requirements">Windows 10 & 11 (64-bit)</p>
              <button
                type="button"
                className="download-btn-primary"
                onClick={() => handleDownload("windows")}
                disabled={downloadStatus.windows === "downloading"}
              >
                <DownloadIcon size={20} />
                {downloadStatus.windows === "downloading" ? "Downloading..." : downloadStatus.windows === "success" ? "Downloaded!" : "Download for Windows"}
              </button>
              <div className="download-card-features">
                <span>✓ Auto-updates</span>
                <span>✓ 64-bit</span>
              </div>
              <div className="download-windows-note">
                <div className="download-windows-note-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                </div>
                <p className="download-windows-note-text">
                  <strong>Note:</strong> For this version, please uninstall the current installed build and folder from <strong>Program Files</strong> and clear the app data before installing.
                </p>
              </div>
            </div>

            {/* Linux */}
            <div className="download-card">
              <div className="download-card-icon-wrap linux">
                <LinuxLogo />
              </div>
              <h3 className="download-card-title">TeamGrid for Linux</h3>
              <p className="download-card-requirements">Ubuntu 20.04+, Fedora, Debian</p>
              <div className="download-card-buttons">
                <button
                  type="button"
                  className="download-btn-outline"
                  onClick={() => handleDownload("ubuntu")}
                  disabled={downloadStatus.ubuntu === "downloading"}
                >
                  <span>.deb Package</span>
                  <span className="download-badge ubuntu">Ubuntu</span>
                  <DownloadIcon size={16} style={{ marginLeft: "auto", color: "#F2673A" }} />
                </button>
                <button
                  type="button"
                  className="download-btn-outline secondary"
                  onClick={() => handleDownload("ubuntu-arm64")}
                  disabled={downloadStatus["ubuntu-arm64"] === "downloading"}
                >
                  <span>.AppImage</span>
                  <DownloadIcon size={16} style={{ marginLeft: "auto", color: "#A0A0A0" }} />
                </button>
              </div>
              <button type="button" className="download-card-link" style={{ textDecoration: "none" }}>
                View checksums
              </button>
            </div>
          </div>
        </section>

        {/* Why Use the Desktop App? - full-bleed background */}
        <section className="download-features-section">
          <div className="download-features-inner">
            <div className="download-features-header">
              <h2 className="download-features-title">Why Use the Desktop App?</h2>
              <p className="download-features-subtitle">Unlock powerful features not available in the web version.</p>
            </div>
            <div className="download-features-grid">
              {desktopFeatures.map((f, i) => (
                <div key={i} className="download-feature-card">
                  <div className="download-feature-icon">
                    <f.icon size={20} stroke="#0f766e" strokeWidth={2} />
                  </div>
                  <div>
                    <h4>{f.title}</h4>
                    <p>{f.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        {/* Quick Installation Guide */}
        <section className="download-guide-section">
          <div className="download-guide-inner">
            <h2 className="download-guide-title">Quick Installation Guide</h2>
            <div className="download-guide-steps">
              {installSteps.map((s) => (
                <div key={s.num} className="download-guide-step">
                  <div className={`download-guide-step-num download-guide-step-num--${s.style}`}>{s.num}</div>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
        </div>

        {/* Security Footer - full-bleed background */}
        <section className="download-security-footer">
          <div className="download-security-inner">
            {securityItems.map((item, i) => (
              <div key={i} className="download-security-item">
                <div className="download-security-icon-wrap">
                  <item.icon size={24} stroke={TEAL} strokeWidth={2} />
                </div>
                <div>
                  <h4>{item.title}</h4>
                  <p>{item.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ - full-bleed background */}
        <section className="download-faq-section">
          <div className="download-faq-inner">
            <h2 className="download-faq-title">Frequently Asked Questions</h2>
            <div className="download-faq-list">
              {faqItems.map((faq, i) => (
                <div key={i} className="download-faq-item">
                  <button
                    type="button"
                    onClick={() => toggleFaq(i)}
                    className="download-faq-summary"
                  >
                    <h3 className="download-faq-question">{faq.question}</h3>
                    <span className="download-faq-arrow flex-shrink-0 flex items-center justify-center w-8 h-8">
                      {faqOpen[i] ? (
                        <Minus size={24} strokeWidth={2} />
                      ) : (
                        <Plus size={24} strokeWidth={2} />
                      )}
                    </span>
                  </button>
                  {faqOpen[i] && (
                    <p className="download-faq-answer">{faq.answer}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Ready to Understand */}
        <ReadyToTransform
          title="Ready to Understand Your Team?"
          description="Join hundreds of teams using TeamGrid to make smarter decisions and build better workplaces."
          // primaryButtonText="Start Free Trial"
          primaryButtonText="Register"
          primaryButtonLink="/register"
          secondaryButtonText="Talk to Sales"
          secondaryButtonLink="/contact-us"
          showAdditionalInfo={true}
        />
      </div>
      <Footer />
    </div>
    </>
  );
};

export default DownloadPage;
